/* ═══════════════════════════════════════════════════════════════
   YH Store Locator — inventory-verified store finder
   Loads the scan-generated feed (yh-store-locator-feed.json),
   renders a Leaflet map + searchable store list, and deep-links
   every product to the dispensary's live menu.

   Graceful degradation: if Leaflet fails to load (CDN blocked),
   the search + list still work; only the map hides.
   ═══════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  var root = document.querySelector('[data-yhsl]');
  if (!root) return;

  var els = {
    map: root.querySelector('[data-yhsl-map]'),
    list: root.querySelector('[data-yhsl-list]'),
    search: root.querySelector('[data-yhsl-search]'),
    strain: root.querySelector('[data-yhsl-strain]'),
    state: root.querySelector('[data-yhsl-state]'),
    stateWrap: root.querySelector('[data-yhsl-state-wrap]'),
    near: root.querySelector('[data-yhsl-near]'),
    reset: root.querySelector('[data-yhsl-reset]'),
    count: root.querySelector('[data-yhsl-count]'),
    freshness: root.querySelector('[data-yhsl-freshness]')
  };

  var state = {
    feed: null,
    stores: [],
    filtered: [],
    userLoc: null,     // {lat, lng} after "Near me"
    activeStore: null, // store id highlighted on map/list
    map: null,
    markers: {},       // store id -> Leaflet marker
    // Mobile card batching
    mobileBatch: 1,    // current batch number (1-indexed)
    mobilePageSize: 4, // stores revealed per batch
    isMobile: false    // window.matchMedia('(max-width: 749px)')
  };

  var mapHeight = parseInt(root.getAttribute('data-map-height'), 10) || 520;
  root.style.setProperty('--yhsl-map-height', mapHeight + 'px');

  // ─── Utilities ───────────────────────────────────────────────

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function haversineMiles(a, b) {
    var R = 3958.8;
    var dLat = (b.lat - a.lat) * Math.PI / 180;
    var dLng = (b.lng - a.lng) * Math.PI / 180;
    var s = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }

  function money(v) {
    if (v == null) return '';
    return '$' + (Number.isInteger(v) ? v : v.toFixed(2));
  }

  function directionsUrl(s) {
    var dest = s.address ? encodeURIComponent(s.address) : s.lat + ',' + s.lng;
    return 'https://www.google.com/maps/dir/?api=1&destination=' + dest;
  }

  // ─── Filtering ───────────────────────────────────────────────

  function applyFilters() {
    var q = (els.search.value || '').trim().toLowerCase();
    var strain = els.strain.value;
    var st = els.state ? els.state.value : '';

    state.filtered = state.stores.filter(function (s) {
      if (st && s.state !== st) return false;
      if (strain && !s._families[strain]) return false;
      if (q) {
        var hay = (s.name + ' ' + (s.city || '') + ' ' + (s.zip || '') + ' ' + (s.address || '')).toLowerCase();
        if (hay.indexOf(q) === -1) return false;
      }
      return true;
    });

    // Sort: by distance when located, else by product count
    if (state.userLoc) {
      state.filtered.forEach(function (s) {
        s._distance = s.has_geo ? haversineMiles(state.userLoc, s) : Infinity;
      });
      state.filtered.sort(function (a, b) { return a._distance - b._distance; });
    } else {
      state.filtered.sort(function (a, b) { return b.product_count - a.product_count; });
    }

    var anyFilter = q || strain || st || state.userLoc;
    els.reset.hidden = !anyFilter;

    // Reset mobile pagination on any filter change so the first batch always shows
    if (state.isMobile) state.mobileBatch = 1;

    renderCount(strain);
    renderList(strain);
    renderMarkers();
  }

  function resetFilters() {
    els.search.value = '';
    els.strain.value = '';
    if (els.state) {
      var resetState = root.getAttribute('data-reset-state') || '';
      els.state.value = Array.prototype.some.call(els.state.options, function (o) { return o.value === resetState; }) ? resetState : '';
    }
    state.userLoc = null;
    state.stores.forEach(function (s) { delete s._distance; });
    applyFilters();
    if (state.map && (!els.state || !els.state.value)) fitAll();
  }

  // ─── Rendering: count + list ─────────────────────────────────

  function renderCount(strain) {
    var n = state.filtered.length;
    var msg = n + ' store' + (n === 1 ? '' : 's');
    if (strain) msg += ' carrying ' + strain;
    if (state.userLoc) msg += ', nearest first';
    els.count.textContent = msg;
  }

  function productRow(p, highlight) {
    var meta = [];
    if (p.size) meta.push(p.size);
    if (p.thc != null) meta.push(p.thc + '% THC');
    if (p.product_type) meta.push(p.product_type);
    var cls = 'yhsl-product' + (highlight && p.sku_family === highlight ? ' yhsl-product--match' : '');
    var inner =
      (p.image_url ? '<img class="yhsl-product__img" loading="lazy" src="' + esc(p.image_url) + '" alt=\"Your Highness product\" onerror="this.style.visibility=\'hidden\'">' : '<span class="yhsl-product__img"></span>') +
      '<span class="yhsl-product__body">' +
      '<p class="yhsl-product__name">' + esc(p.name) + '</p>' +
      '<p class="yhsl-product__meta">' + esc(meta.join(' · ')) + '</p>' +
      '</span>' +
      '<span class="yhsl-product__price">' + money(p.price) + '</span>';
    if (p.product_url) {
      return '<a class="' + cls + '" href="' + esc(p.product_url) + '" target="_blank" rel="noopener">' + inner + '</a>';
    }
    return '<div class="' + cls + '">' + inner + '</div>';
  }

  function storeCard(s, strain) {
    // Inventory-verified stores show a live product count; static listings
    // (e.g. CA/NV retailer lists) show an "Authorized retailer" badge.
    var badges = s.product_count > 0
      ? '<span class="yhsl-badge yhsl-badge--count">' + s.product_count + ' product' + (s.product_count === 1 ? '' : 's') + '</span>'
      : '<span class="yhsl-badge yhsl-badge--count">Authorized retailer</span>';
    if (s.offers_pickup) badges += '<span class="yhsl-badge">Pickup</span>';
    if (s.offers_delivery) badges += '<span class="yhsl-badge">Delivery</span>';
    if (s.medical && s.recreational) badges += '<span class="yhsl-badge">Med &amp; Rec</span>';
    else if (s.medical) badges += '<span class="yhsl-badge">Medical</span>';

    var dist = (state.userLoc && s._distance != null && isFinite(s._distance))
      ? '<span class="yhsl-card__distance">' + s._distance.toFixed(1) + ' mi</span>' : '';

    var products = strain
      ? s.products.filter(function (p) { return p.sku_family === strain; })
      : s.products;
    var expanded = !!(strain || s._expanded);

    return '<article class="yhsl-card' + (state.activeStore === s.id ? ' yhsl-card--active' : '') + '" data-store="' + esc(s.id) + '">' +
      '<div class="yhsl-card__top" data-card-top>' +
      '<h3 class="yhsl-card__name">' + esc(s.name) + '</h3>' + dist +
      '</div>' +
      '<p class="yhsl-card__addr">' + esc(s.address || s.city || '') + '</p>' +
      '<div class="yhsl-card__badges">' + badges + '</div>' +
      '<div class="yhsl-card__actions">' +
      ((s.brand_menu_url || s.store_url)
        ? '<a href="' + esc(s.brand_menu_url || s.store_url) + '" target="_blank" rel="noopener">View menu</a>'
        : '') +
      '<a href="' + directionsUrl(s) + '" target="_blank" rel="noopener">Directions</a>' +
      (s.phone ? '<a href="tel:' + esc(s.phone.replace(/[^0-9+]/g, '')) + '">' + esc(s.phone) + '</a>' : '') +
      (s.product_count > 0
        ? '<button type="button" class="yhsl-card__toggle" data-toggle>' + (expanded ? 'Hide products' : 'Show products') + '</button>'
        : '') +
      '</div>' +
      (expanded
        ? '<div class="yhsl-products">' + products.map(function (p) { return productRow(p, strain); }).join('') + '</div>'
        : '') +
      '</article>';
  }

  /**
   * HTML for the mobile "Show more" pagination button.
   * @param {number} visible - number of cards currently rendered
   * Returns '' when all stores are visible.
   */
  function showMoreButton(visible) {
    var total = state.filtered.length;
    if (visible >= total) return '';
    return '<button type="button" class="yhsl__btn yhsl__btn--showmore" data-yhsl-showmore>' +
      'Show more (' + (visible + 1) + '–' + Math.min(visible + state.mobilePageSize, total) + ' of ' + total + ')</button>';
  }

  function renderList(strain) {
    if (!state.filtered.length) {
      els.list.innerHTML = '<div class="yhsl__empty">No stores match — try a different city or strain.</div>';
      return;
    }

    var isMobile = state.isMobile;
    var visible = isMobile ? state.mobileBatch * state.mobilePageSize : state.filtered.length;
    var btn = isMobile ? showMoreButton(visible) : '';

    // Ensure the currently selected store is visible on mobile
    if (isMobile && state.activeStore) {
      var activeIdx = state.filtered.findIndex(function (s) { return s.id === state.activeStore; });
      if (activeIdx !== -1 && activeIdx >= visible) {
        var expandedBatch = Math.ceil((activeIdx + 1) / state.mobilePageSize);
        state.mobileBatch = expandedBatch;
        visible = expandedBatch * state.mobilePageSize;
        btn = showMoreButton(visible); // recompute after expanding
      }
    }

    var html = '';
    for (var i = 0; i < state.filtered.length; i++) {
      var s = state.filtered[i];
      var cls = i >= visible ? ' yhsl-card--batch-hidden' : '';
      cls += state.activeStore === s.id ? ' yhsl-card--active' : '';
      html += '<article class="yhsl-card' + cls + '" data-store="' + esc(s.id) + '" data-idx="' + i + '">' +
        storeCardInner(s, strain) + '</article>';
    }
    html += btn;
    els.list.innerHTML = html;
  }

  /**
   * Inner HTML for a store card (without the <article> wrapper).
   * Extracted so renderList can add batch-hidden class on the wrapper.
   */
  function storeCardInner(s, strain) {
    var badges = s.product_count > 0
      ? '<span class="yhsl-badge yhsl-badge--count">' + s.product_count + ' product' + (s.product_count === 1 ? '' : 's') + '</span>'
      : '<span class="yhsl-badge yhsl-badge--count">Authorized retailer</span>';
    if (s.offers_pickup) badges += '<span class="yhsl-badge">Pickup</span>';
    if (s.offers_delivery) badges += '<span class="yhsl-badge">Delivery</span>';
    if (s.medical && s.recreational) badges += '<span class="yhsl-badge">Med &amp; Rec</span>';
    else if (s.medical) badges += '<span class="yhsl-badge">Medical</span>';

    var dist = (state.userLoc && s._distance != null && isFinite(s._distance))
      ? '<span class="yhsl-card__distance">' + s._distance.toFixed(1) + ' mi</span>' : '';

    var products = strain
      ? s.products.filter(function (p) { return p.sku_family === strain; })
      : s.products;
    var expanded = !!(strain || s._expanded);

    return '<div class="yhsl-card__top" data-card-top>' +
      '<h3 class="yhsl-card__name">' + esc(s.name) + '</h3>' + dist +
      '</div>' +
      '<p class="yhsl-card__addr">' + esc(s.address || s.city || '') + '</p>' +
      '<div class="yhsl-card__badges">' + badges + '</div>' +
      '<div class="yhsl-card__actions">' +
      ((s.brand_menu_url || s.store_url)
        ? '<a href="' + esc(s.brand_menu_url || s.store_url) + '" target="_blank" rel="noopener">View menu</a>'
        : '') +
      '<a href="' + directionsUrl(s) + '" target="_blank" rel="noopener">Directions</a>' +
      (s.phone ? '<a href="tel:' + esc(s.phone.replace(/[^0-9+]/g, '')) + '">' + esc(s.phone) + '</a>' : '') +
      (s.product_count > 0
        ? '<button type="button" class="yhsl-card__toggle" data-toggle>' + (expanded ? 'Hide products' : 'Show products') + '</button>'
        : '') +
      '</div>' +
      (expanded
        ? '<div class="yhsl-products">' + products.map(function (p) { return productRow(p, strain); }).join('') + '</div>'
        : '') +
      '</div>';
  }

  // ─── Rendering: map ──────────────────────────────────────────

  function pinIcon(active) {
    return window.L.divIcon({
      className: '',
      html: '<div class="yhsl-pin' + (active ? ' yhsl-pin--active' : '') + '"></div>',
      iconSize: [16, 16],
      iconAnchor: [8, 8]
    });
  }

  function popupHtml(s) {
    var inv = s.product_count > 0
      ? s.product_count + ' Your Highness product' + (s.product_count === 1 ? '' : 's')
      : 'Authorized Your Highness retailer';
    var menuUrl = s.brand_menu_url || s.store_url;
    return '<div class="yhsl-popup">' +
      '<p class="yhsl-popup__name">' + esc(s.name) + '</p>' +
      '<p class="yhsl-popup__meta">' + esc(s.address || s.city || '') + '<br>' + inv + '</p>' +
      (menuUrl
        ? '<a href="' + esc(menuUrl) + '" target="_blank" rel="noopener">View menu →</a>'
        : '<a href="' + directionsUrl(s) + '" target="_blank" rel="noopener">Directions →</a>') +
      '</div>';
  }

  function initMap() {
    if (!els.map || typeof window.L === 'undefined') {
      if (els.map) els.map.parentElement.classList.add('yhsl__map--hidden');
      return;
    }
    state.map = window.L.map(els.map, { scrollWheelZoom: false });

    /* The dark basemap here used to be CARTO's free `dark_all` tiles. CARTO has
       since moved its basemaps behind MapTiler: the same URL still answers 200,
       but the image is a grey "API KEY REQUIRED" placeholder, so the locator
       shows a blank grid with markers on it and nothing errors in the console.
       A key in the section settings restores a real dark basemap; with no key we
       fall back to OpenStreetMap's own tiles and darken them in CSS, so the map
       is always a map. */
    var key = (root.getAttribute('data-map-tile-key') || '').trim();
    var tileUrl, attribution;
    if (key) {
      tileUrl = 'https://api.maptiler.com/maps/basic-dark/{z}/{x}/{y}.png?key=' + encodeURIComponent(key);
      attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://maptiler.com/">MapTiler</a>';
    } else {
      if (els.map.parentElement) els.map.parentElement.classList.add('yhsl__map--osm');
      tileUrl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
      attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
    }
    window.L.tileLayer(tileUrl, { attribution: attribution, maxZoom: 19 }).addTo(state.map);
    fitAll();
  }

  function fitAll() {
    var pts = state.stores.filter(function (s) { return s.has_geo; })
      .map(function (s) { return [s.lat, s.lng]; });
    if (pts.length && state.map) state.map.fitBounds(pts, { padding: [36, 36] });
  }

  function renderMarkers() {
    if (!state.map) return;
    var keep = {};
    state.filtered.forEach(function (s) {
      if (!s.has_geo) return;
      keep[s.id] = true;
      if (!state.markers[s.id]) {
        var m = window.L.marker([s.lat, s.lng], { icon: pinIcon(false) })
          .bindPopup(popupHtml(s))
          .addTo(state.map);
        m.on('click', function () { setActive(s.id, { scrollList: true }); });
        state.markers[s.id] = m;
      } else if (!state.map.hasLayer(state.markers[s.id])) {
        state.markers[s.id].addTo(state.map);
      }
      state.markers[s.id].setIcon(pinIcon(state.activeStore === s.id));
    });
    Object.keys(state.markers).forEach(function (id) {
      if (!keep[id] && state.map.hasLayer(state.markers[id])) {
        state.map.removeLayer(state.markers[id]);
      }
    });
    if (state.filtered.length && state.filtered.some(function (s) { return s.has_geo; })) {
      var pts = state.filtered.filter(function (s) { return s.has_geo; })
        .map(function (s) { return [s.lat, s.lng]; });
      state.map.fitBounds(pts, { padding: [36, 36], maxZoom: 13 });
    }
  }

  /**
   * Select a store: highlight its card + pin, collapse every other card's
   * product list, optionally toggle this card's products / pan the map /
   * scroll the list. Single entry point so card clicks, pill clicks, and
   * marker clicks all behave consistently.
   */
  function setActive(id, opts) {
    opts = opts || {};
    state.activeStore = id;

    // Selecting a store collapses all other expanded product lists
    state.stores.forEach(function (s) {
      if (s.id !== id) s._expanded = false;
    });
    if (opts.toggleProducts) {
      var target = state.stores.find(function (x) { return x.id === id; });
      if (target) target._expanded = !target._expanded;
    }

    renderList(els.strain.value);

    Object.keys(state.markers).forEach(function (mid) {
      state.markers[mid].setIcon(pinIcon(mid === id));
    });

    if (opts.scrollList) {
      var card = els.list.querySelector('[data-store="' + id + '"]');
      if (card) card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    if (opts.panMap && state.map) {
      var s = state.stores.find(function (x) { return x.id === id; });
      if (s && s.has_geo) {
        state.map.setView([s.lat, s.lng], Math.max(state.map.getZoom(), 11), { animate: true });
        if (state.markers[id]) state.markers[id].openPopup();
      }
    }
  }

  // ─── Events ──────────────────────────────────────────────────

  function bindEvents() {
    var debounce;
    els.search.addEventListener('input', function () {
      clearTimeout(debounce);
      debounce = setTimeout(applyFilters, 160);
    });

    els.strain.addEventListener('change', applyFilters);
    if (els.state) els.state.addEventListener('change', applyFilters);
    els.reset.addEventListener('click', resetFilters);

    els.near.addEventListener('click', function () {
      if (!navigator.geolocation) return;
      els.near.disabled = true;
      navigator.geolocation.getCurrentPosition(
        function (pos) {
          state.userLoc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          els.near.disabled = false;
          applyFilters();
        },
        function () {
          els.near.disabled = false;
          els.count.textContent = 'Location unavailable — showing all stores.';
        },
        { timeout: 8000 }
      );
    });

    // Mobile "Show more" + card interactions (delegated)
    els.list.addEventListener('click', function (e) {
      // "Show more" button — reveal the next batch
      var onShowMore = e.target.closest('[data-yhsl-showmore]');
      if (onShowMore) {
        state.mobileBatch++;
        renderList(els.strain.value);
        return;
      }

      var card = e.target.closest('.yhsl-card');
      if (!card) return;
      var id = card.getAttribute('data-store');
      var store = state.stores.find(function (s) { return s.id === id; });
      if (!store) return;

      if (e.target.closest('a')) return; // let links through

      var onToggle = e.target.closest('[data-toggle]');
      var onPill = e.target.closest('.yhsl-badge--count');
      if ((onToggle || onPill) && store.product_count > 0) {
        setActive(id, { toggleProducts: true });
        return;
      }

      setActive(id, { panMap: true });
    });

    // Detect mobile viewport for responsive pagination
    function updateMobileState() {
      var wasMobile = state.isMobile;
      state.isMobile = window.matchMedia('(max-width: 749px)').matches;
      if (wasMobile !== state.isMobile) {
        // Leaving mobile → reset batch so next mobile entry starts fresh
        if (!state.isMobile) state.mobileBatch = 1;
        renderList(els.strain.value);
      }
    }
    updateMobileState(); // initial check
    window.addEventListener('resize', updateMobileState);
  }

  // ─── Boot ────────────────────────────────────────────────────

  function populateStrains() {
    var frag = document.createDocumentFragment();
    (state.feed.products || []).forEach(function (p) {
      var opt = document.createElement('option');
      opt.value = p.sku_family;
      opt.textContent = p.sku_family + ' (' + p.store_count + ')';
      frag.appendChild(opt);
    });
    els.strain.appendChild(frag);
  }

  function populateStates() {
    if (!els.state || !els.stateWrap) return;
    var states = state.feed.states ||
      Object.keys(state.stores.reduce(function (m, s) { if (s.state) m[s.state] = 1; return m; }, {})).sort();
    if (states.length < 2) return; // single-state feed: keep the control hidden
    states.forEach(function (st) {
      var opt = document.createElement('option');
      opt.value = st;
      opt.textContent = st;
      els.state.appendChild(opt);
    });
    els.stateWrap.hidden = false;
  }

  function showFreshness() {
    if (els.freshness && root.getAttribute('data-availability-kind') === 'directory') {
      els.freshness.textContent = 'Retailer directory — confirm current menus with each store.';
      els.freshness.hidden = false;
      return;
    }
    if (!state.feed.source_scan_at || !els.freshness) return;
    var d = new Date(state.feed.source_scan_at);
    if (isNaN(d)) return;
    els.freshness.textContent = 'Inventory verified ' + d.toLocaleDateString(undefined, {
      month: 'long', day: 'numeric', year: 'numeric'
    });
    els.freshness.hidden = false;
  }

  function boot() {
    els.list.innerHTML = '<div class="yhsl__loading">Loading stores…</div>';
    fetch(root.getAttribute('data-feed-url'))
      .then(function (r) {
        if (!r.ok) throw new Error('feed ' + r.status);
        return r.json();
      })
      .then(function (feed) {
        state.feed = feed;
        state.stores = (feed.stores || []).map(function (s) {
          // Pre-index strain families per store for O(1) filter checks
          s._families = {};
          (s.products || []).forEach(function (p) { s._families[p.sku_family] = true; });
          return s;
        });
        populateStrains();
        populateStates();
        showFreshness();
        bindEvents();

        // Pre-filter to the section's default state (e.g. NV on the Nevada page)
        var defaultState = root.getAttribute('data-default-state');
        if (defaultState && els.state &&
            Array.prototype.some.call(els.state.options, function (o) { return o.value === defaultState; })) {
          els.state.value = defaultState;
        }

        // URL params override defaults: ?strain=Bluephoria&state=MA&q=boston
        // — lets strain chips, marketing links, and QR codes land pre-filtered.
        try {
          var params = new URLSearchParams(window.location.search);
          var pStrain = params.get('strain');
          if (pStrain && Array.prototype.some.call(els.strain.options, function (o) {
            return o.value.toLowerCase() === pStrain.toLowerCase();
          })) {
            Array.prototype.forEach.call(els.strain.options, function (o) {
              if (o.value.toLowerCase() === pStrain.toLowerCase()) els.strain.value = o.value;
            });
          }
          var pState = params.get('state');
          if (pState && els.state &&
              Array.prototype.some.call(els.state.options, function (o) { return o.value === pState.toUpperCase(); })) {
            els.state.value = pState.toUpperCase();
          }
          var pQ = params.get('q');
          if (pQ) els.search.value = pQ;
        } catch (e) { /* old browsers: defaults stand */ }

        var startMap = function () { initMap(); applyFilters(); };
        if (els.map && typeof window.L === 'undefined') {
          // Leaflet loads with defer — wait for it briefly, then degrade
          var tries = 0;
          var t = setInterval(function () {
            if (typeof window.L !== 'undefined' || ++tries > 40) {
              clearInterval(t);
              startMap();
            }
          }, 125);
        } else {
          startMap();
        }
      })
      .catch(function (err) {
        els.list.innerHTML = '<div class="yhsl__error">Store list is being updated — please check back shortly.</div>';
        if (window.console) console.error('[yhsl] feed load failed:', err);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
